import { useState, type FormEvent } from 'react';
import { useAuth, useUser } from '../../auth/AuthProvider';
import { ROLE_LABELS } from '../../data/domain';
import type { Role, User } from '../../data/types';
import { Modal } from '../../ui/Modal';
import { Icon } from '../../ui/Icon';
import { EmptyState, PageHeading, PanelHeader, View } from '../../ui/View';
import { createAccount, temporaryPassword, updateAccount, useAccounts, type AccountInput } from './usersApi';

type AccountValues = Omit<AccountInput, 'technicianId' | 'technicianEmail'>;

function securityLabel(account: User) {
  if (account.signInLinked) return 'Sign-in linked';
  if (account.hasPassword) return account.forcePasswordChange ? 'Password change required' : 'Password set';
  return 'Invited, not signed in yet';
}

export function UsersView() {
  const me = useUser();
  const { openPassword, refreshUser, config } = useAuth();
  const localLogin = config?.localLogin ?? true;
  const { accounts, error, reload } = useAccounts(true);
  const [editing, setEditing] = useState<User | 'new' | null>(null);

  async function save(account: User | null, values: AccountValues) {
    if (account) await updateAccount(account, values);
    else await createAccount(values);
    await reload();
    if (account?.id === me.id) await refreshUser();
    if (!account) {
      window.alert(values.password
        ? `Account created for ${(values.username || values.email).trim().toLowerCase()}. They must change their temporary password on first sign-in.`
        : `${values.displayName} is invited. They sign in with ${config?.providerName || 'their account'} using ${values.email.trim().toLowerCase()}.`);
    }
    setEditing(null);
  }

  async function deactivate(account: User) {
    if (!window.confirm(`Remove sign-in access for ${account.displayName}? Their job documents and compliance records will remain available.`)) return;
    try { await updateAccount(account, { active: false }); await reload(); } catch (failure) { window.alert((failure as Error).message); }
  }

  async function reset(account: User) {
    const password = temporaryPassword();
    try {
      await updateAccount(account, { password });
      await reload();
      window.alert(`Temporary password for ${account.displayName}: ${password}\n\nShare it securely. They must change it when they next sign in.`);
    } catch (failure) { window.alert((failure as Error).message); }
  }

  return (
    <View id="users">
      <PageHeading eyebrow="Operations" title="Users" subtitle="Manage administrator and technician sign-in access.">
        {me.hasPassword && <button className="btn btn-secondary" id="usersChangePassword" onClick={openPassword}>Change my password</button>}
        <button className="btn btn-primary" id="addUser" onClick={() => setEditing('new')}><Icon name="plus" size={16} />Add user</button>
      </PageHeading>
      <section className="panel">
        <PanelHeader title="Account directory" summary="Technician accounts can also be created from the technician register." />
        {error ? <EmptyState>{error}</EmptyState> : (
          <>
            <div className="table-head user-columns" aria-hidden="true"><span>User</span><span>Role</span><span>Access</span><span>Security</span><span /></div>
            <div className="row-list" id="userList">
              {accounts.map((account) => (
                <article className="row user-columns" key={account.id}>
                  <span className="row-text"><b>{account.displayName}</b><small>{account.email || account.username}</small></span>
                  <span><span className={`badge ${account.role === 'admin' ? 'badge-accent' : account.role === 'operations' ? 'badge-info' : 'badge-neutral'}`}>{ROLE_LABELS[account.role] || 'Technician'}</span></span>
                  <span><span className={`badge ${account.active ? 'badge-success' : 'badge-danger'}`}>{account.active ? 'Active' : 'Removed'}</span></span>
                  <small className="row-meta">{securityLabel(account)}</small>
                  <span className="button-group">
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditing(account)}>Edit</button>
                    {account.active && localLogin && <button className="btn btn-secondary btn-sm" onClick={() => reset(account)}>Reset</button>}
                    {account.active && account.role !== 'admin' && <button className="btn btn-danger btn-sm" onClick={() => deactivate(account)}>Remove</button>}
                  </span>
                </article>
              ))}
            </div>
          </>
        )}
      </section>
      {editing && <UserModal account={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSave={save} />}
    </View>
  );
}

function UserModal({ account, onClose, onSave }: { account: User | null; onClose(): void; onSave(account: User | null, values: AccountValues): Promise<void> }) {
  const { config } = useAuth();
  const localLogin = config?.localLogin ?? true;
  const providerLogin = config?.oidc ?? false;
  const [displayName, setDisplayName] = useState(account?.displayName ?? '');
  const [username, setUsername] = useState(account?.username ?? '');
  const [email, setEmail] = useState(account?.email ?? '');
  const [role, setRole] = useState<Role>(account?.role ?? 'tech');
  const [active, setActive] = useState(account?.active ?? true);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password && password.length < 12) return setError('Temporary passwords must be at least 12 characters.');
    if (!account && !password && !email.trim()) return setError(providerLogin ? 'Enter the email address they will sign in with.' : 'Enter a temporary password of at least 12 characters.');
    try { await onSave(account, { displayName, username: localLogin ? username : account?.username ?? '', email, password, role, active }); }
    catch (failure) { setError((failure as Error).message); }
  }

  return (
    <Modal id="userModal" size="sm" onClose={onClose} labelledBy="userModalTitle">
      <form onSubmit={submit}>
        <p className="eyebrow">User account</p>
        <h2 id="userModalTitle">{account ? 'Edit user' : 'Add user'}</h2>
        <label>Full name <input id="newUserName" autoComplete="name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoFocus /></label>
        <label>Email{providerLogin ? ' (used to sign in)' : ''} <input id="newUserEmail" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        {localLogin && <label>Username <input id="newUsername" autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} /></label>}
        <div className="form-row">
          <label>Role <select id="newUserRole" value={role} onChange={(event) => setRole(event.target.value as Role)}><option value="tech">Technician</option><option value="operations">Operations</option><option value="admin">Administrator</option></select></label>
          <label>Access <select id="newUserAccess" value={active ? 'active' : 'inactive'} onChange={(event) => setActive(event.target.value === 'active')}><option value="active">Active</option><option value="inactive">Removed</option></select></label>
        </div>
        {localLogin && <label>{account ? 'Reset password (optional) ' : `Temporary password${providerLogin ? ' (optional)' : ''} `}<input id="newUserPassword" type="password" autoComplete="new-password" placeholder={account ? 'Leave blank to keep current password' : 'At least 12 characters'} value={password} onChange={(event) => setPassword(event.target.value)} /></label>}
        <p className="form-error" id="userError" aria-live="polite">{error}</p>
        <div className="form-actions"><button className="btn btn-primary" id="saveUserButton" type="submit">{account ? 'Save account' : 'Create account'}</button></div>
      </form>
    </Modal>
  );
}
